import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Percent, PhoneOff, ArrowLeft, Search, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { TYPE_PATHS } from "@/lib/format";
import { ListingCard } from "@/components/ListingCard";
import { useSite, asset } from "@/context/SiteContext";

const SECTIONS = [
  { type: "auction", to: "/auctions" },
  { type: "sale", to: "/sale" },
  { type: "rent", to: "/rent" },
  { type: "job", to: "/jobs" },
  { type: "invest", to: "/invest" },
];

const HeroSearch = ({ site }) => {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [t, setT] = useState("auction");
  const go = (e) => { e.preventDefault(); nav(`${TYPE_PATHS[t]}${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`); };
  return (
    <form onSubmit={go} className="bg-white rounded-xl p-2 grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 shadow-lg max-w-2xl" data-testid="hero-search">
      <select data-testid="hero-search-section" value={t} onChange={(e) => setT(e.target.value)} className="h-11 rounded-lg border border-slate-200 px-3 text-sm text-slate-800 bg-slate-50">
        {SECTIONS.map((s) => <option key={s.type} value={s.type}>{site[`${s.type}_title`]}</option>)}
      </select>
      <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن شاحنة، سجل تجاري، معدات..." className="h-11 rounded-lg border border-slate-200 px-3 text-sm text-slate-900 outline-none focus:border-brand" />
      <button data-testid="hero-search-btn" className="h-11 rounded-lg bg-sun hover:bg-sun-dark text-white font-bold px-6 flex items-center justify-center gap-2 text-sm transition-colors"><Search size={16} />بحث</button>
    </form>
  );
};

const Hero = ({ site, stats }) => (
  <section className="bg-slate-900 text-white relative overflow-hidden">
    <div className="absolute inset-y-0 left-0 w-1/2 hidden lg:block">
      <img src={asset(site.hero_image)} alt="" data-testid="hero-image" className="w-full h-full object-cover opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-l from-slate-900 via-slate-900/60 to-transparent" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
      <div className="max-w-2xl fade-up">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-md text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /><span data-testid="hero-badge">{site.hero_badge}</span>
        </span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight mb-4">
          <span data-testid="hero-title">{site.hero_title} <span className="text-sun">{site.hero_highlight}</span> {site.hero_title_end}</span>
        </h1>
        <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8" data-testid="hero-subtitle">{site.hero_subtitle}</p>
        <HeroSearch site={site} />
      </div>
      <div className="mt-12 grid grid-cols-2 sm:grid-cols-5 gap-y-5 max-w-3xl border-t border-white/10 pt-6" data-testid="hero-stats">
        {SECTIONS.map((s) => (
          <Link key={s.type} to={s.to} className="group" data-testid={`hero-stat-${s.type}`}>
            <div className="text-2xl font-bold tabular-nums font-mono">{stats?.[s.type] ?? 0}</div>
            <div className="text-xs text-slate-400 group-hover:text-white transition-colors">{site[`${s.type}_title`]}</div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const SectionTiles = ({ stats, site }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
    <div className="flex items-end justify-between mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{site.sections_title}</h2>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {SECTIONS.map((s) => (
        <Link key={s.type} to={s.to} data-testid={`section-tile-${s.type}`} className="group bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:border-slate-300 transition-all">
          <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
            <img src={asset(site[`${s.type}_image`])} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900">{site[`${s.type}_title`]}</h3>
              <span className="text-xs font-semibold text-slate-500 tabular-nums">{stats?.[s.type] ?? 0} إعلان</span>
            </div>
            <p className="text-sm text-slate-500 mt-1 line-clamp-1">{site[`${s.type}_desc`]}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand">تصفّح القسم <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /></span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const Trust = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="bg-white border border-slate-200 rounded-xl grid md:grid-cols-4 divide-y md:divide-y-0 md:divide-x md:divide-x-reverse divide-slate-200">
      {[
        { icon: ShieldCheck, t: "مراجعة الإدارة", d: "كل إعلان ومزاد يُراجع ويُعتمد قبل النشر." },
        { icon: BadgeCheck, t: "بائعون موثقون", d: "البائع يُوثّق بالسجل التجاري والباركود." },
        { icon: PhoneOff, t: "خصوصية الأرقام", d: "لا تظهر أرقام التواصل إلا بعد موافقة الإدارة." },
        { icon: Percent, t: "عمولة 1% فقط", d: "تُحتسب على السعر النهائي للمزاد فقط." },
      ].map((x) => (
        <div key={x.t} className="p-5">
          <div className="flex items-center gap-2 font-bold text-sm text-slate-900 mb-1"><x.icon size={18} className="text-brand" />{x.t}</div>
          <p className="text-sm text-slate-500 leading-relaxed">{x.d}</p>
        </div>
      ))}
    </div>
  </section>
);

export default function Home() {
  const site = useSite();
  const [stats, setStats] = useState(null);
  const [auctions, setAuctions] = useState([]);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/listings", { params: { type: "auction", limit: 4, active: true } }).then((r) => setAuctions(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="home-page">
      <Hero site={site} stats={stats} />
      <SectionTiles stats={stats} site={site} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">مزادات قائمة</h2>
          <Link to="/auctions" className="text-slate-700 hover:text-brand font-semibold text-sm flex items-center gap-1" data-testid="home-all-auctions">عرض كل المزادات <ArrowLeft size={14} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{auctions.map((l) => <ListingCard key={l.id} l={l} />)}</div>
      </section>
      <Trust />
    </div>
  );
}
